<template>
  <div v-if="hasHelp" class="relative inline-block">
    <!-- Toggle button — drop this into a StandardToolPage #actions slot. -->
    <AppButton
      type="button"
      :label="$t('ui.tool_help.button')"
      :icon="mdiInformationOutline"
      :variant="isOpen ? 'solid' : 'outline'"
      :tone="isOpen ? 'primary' : 'neutral'"
      size="sm"
      :aria-expanded="isOpen"
      :aria-controls="panelId"
      :title="$t('ui.tool_help.button')"
      @click="toggle"
    />

    <!-- Drawer + backdrop. Teleported to body so the surrounding page chrome
         doesn't clip it. Closed by default; toggled by the button above. -->
    <teleport to="body">
      <transition
        enter-active-class="transition-opacity duration-150"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen"
          class="fixed inset-0 z-drawer bg-theme-text/30 dark:bg-theme-text/50"
          aria-hidden="true"
          @click="close"
        />
      </transition>
      <transition
        enter-active-class="transition-transform duration-200 ease-out"
        enter-from-class="translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition-transform duration-150 ease-in"
        leave-from-class="translate-x-0"
        leave-to-class="translate-x-full"
      >
        <aside
          v-if="isOpen"
          :id="panelId"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="`${panelId}-title`"
          class="fixed inset-y-0 right-0 z-drawer-panel flex w-full max-w-md flex-col bg-theme-surface dark:bg-theme-surface-elevated shadow-theme-dialog dark:shadow-theme-shadow/60 border-l border-theme-border dark:border-theme-border"
        >
          <header
            class="flex items-start justify-between gap-3 border-b border-theme-border dark:border-theme-border px-5 py-4"
          >
            <div class="min-w-0">
              <span
                class="block text-[11px] font-semibold uppercase tracking-wide text-theme-palette-emerald dark:text-theme-palette-emerald"
              >
                {{ $t('ui.tool_help.eyebrow') }}
              </span>
              <h2
                :id="`${panelId}-title`"
                class="mt-0.5 text-base font-semibold text-theme-text"
              >
                {{ resolvedTitle }}
              </h2>
            </div>
            <AppIconButton
              :label="$t('ui.tool_help.close')"
              variant="ghost"
              tone="neutral"
              size="md"
              @click="close"
            >
              <icon :path="mdiClose" size="18" />
            </AppIconButton>
          </header>

          <div class="flex-1 overflow-y-auto px-5 py-4 space-y-6 text-sm leading-6 text-theme-text-secondary">
            <!-- Priority: explicit #content slot > registry component > registry
                 sections > nothing. The button self-hides when none of these
                 are present, so it's safe to drop into any tool page. -->
            <!-- #lead: page-specific text that answers why the drawer was opened
                 (e.g. a result's caveat), above the tool's general help. -->
            <slot name="lead" />
            <slot name="content">
              <component :is="helpComponent" v-if="helpComponent" />
              <tool-help-sections v-else-if="helpSections" :sections="helpSections" />
            </slot>
            <slot name="details" />
          </div>
          <footer
            v-if="$slots.footer"
            class="border-t border-theme-border px-5 py-4 dark:border-theme-border"
          >
            <slot name="footer" />
          </footer>
        </aside>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AppIconButton from '@/components/ui/buttons/AppIconButton.vue';
  import ToolHelpSections from '@/components/ui/help/ToolHelpSections.vue';
  import { getToolHelp } from '@/components/ui/help/toolHelpRegistry';
  import { mdiClose, mdiInformationOutline } from '@mdi/js';
  import { computed, onBeforeUnmount, onMounted, ref, useSlots, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps({
    toolKey: { type: String, required: true },
    /** Optional override for the drawer header title. Falls back to the
     *  tool's `page_titles.<slug>` i18n key. */
    title: { type: String, default: '' },
  });

  const { t, te } = useI18n();
  const slots = useSlots();
  const isOpen = ref(false);
  const panelId = `tool-help-${Math.random().toString(36).slice(2, 10)}`;

  // Resolve help content for this tool from the registry: a custom component
  // or a declarative `sections` descriptor. An explicit `#content` slot always
  // wins. With none of these, `hasHelp` is false and the button hides itself.
  const helpEntry = computed(() => getToolHelp(props.toolKey));
  const helpComponent = computed(() => helpEntry.value?.component || null);
  const helpSections = computed(() => helpEntry.value?.sections || null);
  const hasHelp = computed(
    () =>
      !!slots.content ||
      !!slots.lead ||
      !!slots.details ||
      !!slots.footer ||
      !!helpComponent.value ||
      !!helpSections.value
  );

  const resolvedTitle = computed(() => {
    if (props.title) return props.title;
    const key = `page_titles.${props.toolKey.replace(/-/g, '_')}`;
    const teSafe = typeof te === 'function' ? te : () => true;
    return teSafe(key) ? t(key) : t('ui.tool_help.button');
  });

  function open() {
    isOpen.value = true;
  }
  function close() {
    isOpen.value = false;
  }
  function toggle() {
    isOpen.value ? close() : open();
  }

  // A page can open the drawer from its own content (e.g. a "What does this
  // figure mean?" link next to a result).
  defineExpose({ open, close });

  function onKeydown(e) {
    if (e.key === 'Escape' && isOpen.value) close();
  }
  onMounted(() => window.addEventListener('keydown', onKeydown));
  onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));

  // The shared BenchmarkListPage instance is reused across benchmark types
  // (imbalance ↔ bidding ↔ revenue) — when toolKey swaps under us we must
  // force-close any open drawer so the user doesn't see stale content from
  // the previous type.
  watch(
    () => props.toolKey,
    () => {
      if (isOpen.value) close();
    }
  );
</script>
